import type { ReactNode } from "react";

/**
 * QuestionOption — shared per-option primitive consumed by both the
 * landing `InlineQuizPreview` (presentational) and the live `QuizPlayer`
 * (interactive). Spec: docs/09-adr/adr-019-sprint10-shared-question-option-primitive.md.
 *
 * Visual contract:
 *   <wrapper class="answer-card …">
 *     <span class="answer-card__numeral" {aria-hidden when presentational}>{numeral}</span>
 *     <span class="flex-1 text-ink">{label}</span>
 *     {trailing}
 *   </wrapper>
 *
 * The wrapper is a presentational `<span class="answer-card cursor-default">`
 * when `onSelect` is omitted, or an interactive `<button class="answer-card">`
 * with `disabled` + `aria-pressed` + arbitrary `data-*` passthroughs when
 * `onSelect` is provided.
 *
 * Per ADR-019 (zero-diff target), the trailing slot is a passthrough — the
 * primitive does NOT wrap it in any trailing-wrapper element. The
 * consumer renders whatever element it had before. This keeps the rendered
 * DOM byte-identical to pre-refactor on both call sites.
 *
 * Per ADR-019 Open Q4 (resolved during impl), `aria-hidden` on the numeral
 * is rendered only on the presentational variant (where the numeral is
 * decorative). On the interactive variant the numeral is part of the button's
 * accessible name (consistent with the live quiz's pre-refactor markup),
 * so we omit `aria-hidden` there.
 *
 * Do not add per-option layout, click handling, or analytics to this file.
 * Consumers own those concerns. This primitive only owns the per-option
 * visual contract.
 */

type SharedProps = {
  /** Letter or number rendered in the leading bubble. "A", "B", "1", "2". */
  readonly numeral: string;
  /** Visible label text. */
  readonly label: string;
  /** Right-hand affordance slot (selected checkmark, weight badge, etc.). */
  readonly trailing?: ReactNode;
};

type PresentationalProps = SharedProps & {
  readonly onSelect?: undefined;
  readonly selected?: undefined;
  readonly disabled?: undefined;
  /** Arbitrary data-* attrs forwarded to the wrapper. */
  readonly dataAttrs?: Readonly<Record<string, string | boolean | number>>;
};

type InteractiveProps = SharedProps & {
  /** Triggers the selection. Required for the interactive variant. */
  readonly onSelect: () => void;
  /** Visual + ARIA selected state. */
  readonly selected?: boolean;
  /** Disables the button. */
  readonly disabled?: boolean;
  /** Arbitrary data-* attrs forwarded to the <button>. */
  readonly dataAttrs?: Readonly<Record<string, string | boolean | number>>;
};

export type QuestionOptionProps = PresentationalProps | InteractiveProps;

/**
 * `data-*` map normalizer. Anything in `dataAttrs` is forwarded verbatim
 * to the wrapper element. Keys must already be in `data-*` form.
 */
function spreadDataAttrs(
  dataAttrs?: Readonly<Record<string, string | boolean | number>>,
): Record<string, string | boolean | number> {
  if (!dataAttrs) return {};
  const out: Record<string, string | boolean | number> = {};
  for (const [k, v] of Object.entries(dataAttrs)) {
    if (k.startsWith("data-")) out[k] = v;
  }
  return out;
}

export function QuestionOption(props: QuestionOptionProps) {
  const { numeral, label, trailing, dataAttrs } = props;
  const dataProps = spreadDataAttrs(dataAttrs);

  // Interactive case: render <button>.
  if (props.onSelect) {
    const { onSelect, selected, disabled } = props;
    return (
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        aria-pressed={selected ?? false}
        className="answer-card disabled:cursor-not-allowed disabled:opacity-70"
        {...dataProps}
      >
        <span className="answer-card__numeral">{numeral}</span>
        <span className="flex-1 text-ink">{label}</span>
        {trailing}
      </button>
    );
  }

  // Presentational case: render <span>. No click handler, no ARIA pressed.
  return (
    <span className="answer-card cursor-default" {...dataProps}>
      <span className="answer-card__numeral" aria-hidden="true">
        {numeral}
      </span>
      <span className="flex-1 text-ink">{label}</span>
      {trailing}
    </span>
  );
}
